import React, { useState, useEffect } from 'react';
import { Box, Card, CardContent, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, InputAdornment, CircularProgress, List, ListItem, ListItemText, ListItemSecondaryAction, Divider,  } from '@mui/material';
import {
  Add as AddIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  ContentCut as TailorIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import client from '../../api/client';
import CustomTextField from '../../components/CustomTextField';
import { toPersianDigits } from '../../utils/numberConverter';

const themeColors = {
  dark: '#1a1a2e',
  gold: '#d4af37',
  goldLight: '#f4e4bc',
};

const Tailors = () => {
  const [tailors, setTailors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTailor, setEditingTailor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    full_name: '',
    mobile: '',
  });

  useEffect(() => {
    fetchTailors();
  }, []);

  const fetchTailors = async () => {
    try {
      const response = await client.get('/orders/tailors/');
      setTailors(response.data);
    } catch (error) {
      toast.error('خطا در دریافت لیست خیاط‌ها');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (tailor = null) => {
    if (tailor) {
      setEditingTailor(tailor);
      setForm({
        full_name: tailor.full_name,
        mobile: tailor.mobile,
      });
    } else {
      setEditingTailor(null);
      setForm({ full_name: '', mobile: '' });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingTailor(null);
    setForm({ full_name: '', mobile: '' });
  };

  const handleSubmit = async () => {
    if (!form.full_name.trim()) {
      toast.error('نام خیاط الزامی است');
      return;
    }
    if (!/^09\d{9}$/.test(form.mobile)) {
      toast.error('شماره موبایل باید ۱۱ رقم و با ۰۹ شروع شود');
      return;
    }

    setSaving(true);
    try {
      if (editingTailor) {
        await client.put(`/orders/tailors/${editingTailor.id}/`, form);
        toast.success('خیاط ویرایش شد');
      } else {
        await client.post('/orders/tailors/create/', form);
        toast.success('خیاط اضافه شد');
      }
      fetchTailors();
      handleCloseDialog();
    } catch (error) {
      toast.error(error.response?.data?.error || 'خطا در ذخیره');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (tailor) => {
    if (!window.confirm(`آیا از حذف "${tailor.full_name}" مطمئن هستید؟`)) return;
    
    try {
      await client.delete(`/orders/tailors/${tailor.id}/`);
      toast.success('خیاط حذف شد');
      fetchTailors();
    } catch (error) {
      toast.error('خطا در حذف');
    }
  };

  

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress sx={{ color: themeColors.gold }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, pb: 10 }}>
      {/* هدر */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <TailorIcon sx={{ color: themeColors.gold }} />
        <Typography variant="h6" fontWeight="bold">
          خیاط‌ها
        </Typography>
        <Typography variant="body2" color="text.secondary">
          ({toPersianDigits(tailors.length.toString())})
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            bgcolor: themeColors.gold,
            color: themeColors.dark,
            '&:hover': { bgcolor: themeColors.goldLight },
          }}
        >
          افزودن خیاط
        </Button>
      </Box>

      {/* لیست خیاط‌ها */}
      {tailors.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <TailorIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography color="text.secondary">
              هنوز خیاطی ثبت نشده
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              sx={{ mt: 2, borderColor: themeColors.gold, color: themeColors.gold }}
            >
              افزودن اولین خیاط
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <List disablePadding>
            {tailors.map((tailor, index) => (
              <React.Fragment key={tailor.id}>
                {index > 0 && <Divider />}
                <ListItem>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon sx={{ color: themeColors.gold, fontSize: 20 }} />
                        <Typography fontWeight="bold">{tailor.full_name}</Typography>
                      </Box>
                    }
                    secondary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" dir="ltr">
                          {toPersianDigits(tailor.mobile)}
                        </Typography>
                        {tailor.orders_count > 0 && (
                          <Typography variant="caption" color="primary" sx={{ mr: 1 }}>
                            • {toPersianDigits(tailor.orders_count.toString())} سفارش
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton size="small" onClick={() => handleOpenDialog(tailor)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(tailor)} color="error">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        </Card>
      )}

      {/* دیالوگ افزودن/ویرایش */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="xs" fullWidth>
        <DialogTitle
          sx={{
            bgcolor: themeColors.dark,
            color: themeColors.gold,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="h6">
            {editingTailor ? 'ویرایش خیاط' : 'افزودن خیاط'}
          </Typography>
          <IconButton onClick={handleCloseDialog} sx={{ color: themeColors.gold }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <CustomTextField             fullWidth
            label="نام و نام خانوادگی"
            value={form.full_name}
            onChange={(e) => setForm({ ...form, full_name: e.target.value })}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
          <CustomTextField             fullWidth
            label="شماره موبایل"
            value={form.mobile}
            onChange={(e) => {
              const val = e.target.value.replace(/[^0-9]/g, '').slice(0, 11);
              setForm({ ...form, mobile: val });
            }}
            placeholder="09123456789"
            inputProps={{ dir: 'ltr', style: { textAlign: 'left' } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PhoneIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDialog}>انصراف</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={saving}
            sx={{
              bgcolor: themeColors.gold,
              color: themeColors.dark,
              '&:hover': { bgcolor: themeColors.goldLight },
            }}
          >
            {saving ? <CircularProgress size={24} /> : editingTailor ? 'ذخیره' : 'افزودن'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Tailors;